import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton, TextField, Tooltip } from '@material-ui/core';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';

const API_BASE = '/planner-api';

// Gestão de Grupos/holdings (tipo SGPS) que agregam vários clientes.
// Um grupo tem N clientes; um cliente pertence a 0..1 grupo (atribuído na
// página Clientes). Aqui só se criam/editam/apagam os grupos.
export default function AdminGroups({ token }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [view, setView] = useAdminView('groups');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/client-groups`, { headers });
      setGroups(r.ok ? await r.json() : []);
    } catch { setGroups([]); }
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  async function create() {
    const name = newName.trim();
    if (!name) return;
    await fetch(`${API_BASE}/client-groups`, { method: 'POST', headers, body: JSON.stringify({ name }) });
    setNewName('');
    load();
  }
  async function saveEdit(id) {
    const name = editName.trim();
    if (!name) return;
    await fetch(`${API_BASE}/client-groups/${id}`, { method: 'PUT', headers, body: JSON.stringify({ name }) });
    setEditId(null); setEditName('');
    load();
  }
  async function remove(id) {
    if (!window.confirm('Apagar este grupo? Os clientes ficam sem grupo (não são apagados).')) return;
    await fetch(`${API_BASE}/client-groups/${id}`, { method: 'DELETE', headers });
    load();
  }

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: 'var(--adm-card, #fff)', border: '1px solid var(--adm-card-border, #e2e8f0)', borderRadius: 10 };
  const thS = { textAlign: 'left', padding: '14px 20px', fontSize: '0.66rem', fontWeight: 700, color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))', background: 'var(--adm-thead-bg, #f8fafc)' };
  const tdS = { padding: '13px 20px', fontSize: '0.84rem', color: 'var(--adm-text, #0f172a)', borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))' };
  const editField = (id) => (
    <TextField size="small" variant="outlined" value={editName} autoFocus
      onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(id)}
      style={{ maxWidth: 320 }} inputProps={{ style: { fontSize: '0.9rem', fontWeight: 700 } }} />
  );
  const actions = (g) => (editId === g.id ? (
    <>
      <Tooltip title="Guardar"><IconButton size="small" onClick={() => saveEdit(g.id)}><SaveIcon style={{ fontSize: 19, color: '#16a34a' }} /></IconButton></Tooltip>
      <Tooltip title="Cancelar"><IconButton size="small" onClick={() => { setEditId(null); setEditName(''); }}><CloseIcon style={{ fontSize: 19, color: 'var(--adm-text-dim, #64748b)' }} /></IconButton></Tooltip>
    </>
  ) : (
    <>
      <Tooltip title="Renomear"><IconButton size="small" onClick={() => { setEditId(g.id); setEditName(g.name); }}><EditIcon style={{ fontSize: 18, color: 'var(--adm-text-dim, #475569)' }} /></IconButton></Tooltip>
      <Tooltip title="Apagar grupo"><IconButton size="small" onClick={() => remove(g.id)}><DeleteOutlineIcon style={{ fontSize: 19, color: 'var(--adm-danger, #ef4444)' }} /></IconButton></Tooltip>
    </>
  ));

  return (
    <Box>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22, background: 'var(--adm-header-bg, #e2f1e9)', border: '1px solid var(--adm-header-border, #bfe0d1)', borderRadius: 14, padding: '18px 22px' }}>
        <Box style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(99,102,241,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <GroupWorkIcon style={{ color: '#4338ca' }} />
        </Box>
        <Box>
          <Typography style={{ fontSize: '1.72rem', fontWeight: 800, color: 'var(--adm-text, #0f172a)' }}>Grupos</Typography>
          <Typography style={{ fontSize: '0.82rem', color: 'var(--adm-text-dim, #64748b)' }}>
            Holdings/SGPS que agregam vários clientes. A associação cliente→grupo faz-se na página Clientes.
          </Typography>
        </Box>
      </Box>

      <AdminHint
        nodes={[{ text: 'Grupo', tone: 'slate' }, { text: 'Organizações', tone: 'green' }]}
        labels={['agrega']}
        note="Um Grupo (holding/SGPS) agrega várias Organizações. A associação Organização→Grupo faz-se na página Organizações."
      />

      {/* Criar */}
      <Box style={{ display: 'flex', gap: 8, marginBottom: 20, maxWidth: 560 }}>
        <TextField size="small" variant="outlined" placeholder="Nome do novo grupo (ex.: Grupo Petrotec)…"
          value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()}
          style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.85rem' } }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={create}
          style={{ textTransform: 'none', fontWeight: 700, background: 'var(--adm-brand, #2f5d50)', color: '#fff', borderRadius: 8 }}>
          Criar grupo
        </Button>
      </Box>

      {!loading && groups.length > 0 && (
        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <AdminViewToggle view={view} onChange={setView} />
        </Box>
      )}

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress size={30} /></Box>
      ) : groups.length === 0 ? (
        <Typography style={{ color: 'var(--adm-text-muted, #94a3b8)', padding: 20 }}>Sem grupos. Cria o primeiro acima.</Typography>
      ) : view === 'table' ? (
        <Box style={{ background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>Grupo</th>
              <th style={thS}>Organizações</th>
              <th style={{ ...thS, width: 100 }}></th>
            </tr></thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.id}>
                  <td style={tdS}>
                    {editId === g.id ? editField(g.id) : (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <GroupWorkIcon style={{ fontSize: 20, color: '#4338ca' }} />
                        <Typography style={{ fontSize: '0.87rem', fontWeight: 700, color: 'var(--adm-text, #0f172a)' }}>{g.name}</Typography>
                      </Box>
                    )}
                  </td>
                  <td style={tdS}>
                    <Typography style={{ fontSize: '0.8rem', color: 'var(--adm-text-dim, #64748b)' }}>
                      {g.clientCount} organizaç{g.clientCount !== 1 ? 'ões' : 'ão'}
                      {g.clients?.length ? ` · ${g.clients.map(c => c.name).join(', ')}` : ''}
                    </Typography>
                  </td>
                  <td style={tdS}>{actions(g)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      ) : (
        <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {groups.map(g => (
            <Box key={g.id} style={rowStyle}>
              <GroupWorkIcon style={{ fontSize: 22, color: '#4338ca' }} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                {editId === g.id ? editField(g.id) : (
                  <>
                    <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--adm-text, #0f172a)' }}>{g.name}</Typography>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-text-muted, #94a3b8)' }}>
                      {g.clientCount} cliente{g.clientCount !== 1 ? 's' : ''}
                      {g.clients?.length ? ` · ${g.clients.map(c => c.name).join(', ')}` : ''}
                    </Typography>
                  </>
                )}
              </Box>
              {actions(g)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
